import { useContext, useEffect, useReducer, lazy, Suspense } from "react";
import "../app/App.scss";
import Body from "../features/common/elements/Body";
import { OrgInsightsBody } from "../features/organization-insights/OrgInsightsBody";
import PlanView from "../features/continuous-improvement/export/PlanView";
import LaborMarketExportView from "../features/lightcast/LaborMarketExportView";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import FacultySuccessExportView from "@/features/faculty-success/export/FacultySuccessExportView";
import MissionAndOutcomesExportView from "@/features/strategy/mission-and-outcomes/export/MissionAndOutcomesExportView";
import SllExportView from "@/features/sll/export/SllExportView";
import CesHtmlView from "@/features/ces/export/HtmlView";
import HomeComponent from "@/features/insights/home/HomeComponent";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";
import IncorrectURLComponent from "@/features/insights/IncorrectURLComponent";
import OrgProfileComponent from "@/features/insights/OrgProfileComponent";
import OrgDetailComponent from "@/features/insights/OrgDetailComponent";
import AcademicsAndOffices from "@/features/insights/academicsAndOffices/AcademicsAndOffices";
import { InsightsSettings } from "@/features/insights/settings/InsightsSettings";
import SiteMap from "@/features/insights/SiteMap";
import { InsightsHeaderSidebar } from "@/features/insights/InsightsHeaderSidebar";
import { PlanningHeaderSidebar } from "@/features/insights/PlanningHeaderSidebar";
import { IntlProvider } from "react-intl";
import { pig } from "../static/js/pig.js";
import ENGLISH from "../i18n/languages/en-US.json";
import { AppContext, CesUserRole, OutcomesTerminology } from "../contexts/AppContext";
import Loader from "@/features/common/elements/Loader";
import SkipMenu from "@/features/common/elements/SkipMenu";
import { initiateGainsight } from "../static/js/gainsight_integration";

import {
  Institution,
  UserInstitution,
  Lead,
  Membership,
  defaultInstitution,
  InsightsUser,
  defaultMembership,
  defaultInsightsUser,
  Product
} from "@/features/common/interfaces/Interface";
import { sendRequest } from "@/common/http";
import { NodeAccess } from "@/features/insights/home/ContributorInsights";
import { checkIfProductIntegrated } from "@/features/common/helper";

// Lazy load development-only component
const MirageStateComponent = lazy(() => import("@/mirage/MirageStateComponent"));

interface Action {
  locale: string;
  type: string;
  institution: Institution;
  leads: Lead[];
  component: string;
  membership: Membership;
  showLightcastPreview: boolean;
  user: InsightsUser;
  tab: string;
  integratedProducts: Product[] | null;
  nodeAccessContributor: NodeAccess | null;
  institutionsList: UserInstitution[];
  selectedInstitution: string | null;
  breadcrumb: null | string;
  showUnauthorized: boolean;
  cesUserRole: CesUserRole | null;
  outcomesTerminology: OutcomesTerminology | null;
  successMetricMessage: string | null;
  isChatStreaming: boolean;
  isChatQuotaExhausted: boolean;
  isAiChatToggleVisible: boolean;
}
const defaultAction: Action = {
  locale: "",
  type: "",
  leads: [],
  component: "",
  institution: defaultInstitution,
  membership: defaultMembership,
  showLightcastPreview: false,
  user: defaultInsightsUser,
  tab: "",
  integratedProducts: null,
  nodeAccessContributor: null,
  institutionsList: [],
  selectedInstitution: null,
  breadcrumb: null,
  showUnauthorized: false,
  cesUserRole: null,
  outcomesTerminology: null,
  successMetricMessage: null,
  isChatStreaming: false,
  isChatQuotaExhausted: false,
  isAiChatToggleVisible: false
};

interface State {
  locale: string;
  messages: object;
  institution: Institution;
  user: InsightsUser;
  leads: Lead[];
  membership: Membership;
  tab: string;
  componentLoaded: {
    selectedTab: string;
    collapseSideNav: boolean;
    openPreviewModal: boolean;
    institution: boolean;
    membership: boolean;
  };
  integratedProducts: Product[] | null;
  nodeAccess: boolean;
  nodeAccessContributor: NodeAccess | null;
  institutionsList: UserInstitution[];
  selectedInstitution: string | null;
  breadcrumb: string | null;
  showUnauthorized: boolean;
  cesUserRole: CesUserRole | null;
  outcomesTerminology: OutcomesTerminology | null;
  successMetricMessage: string | null;
  isChatStreaming: boolean;
  isChatQuotaExhausted: boolean;
  isAiChatToggleVisible: boolean;
}
const reducer = (state: State, action: Action) => {
  const componentLoaded = state.componentLoaded;
  switch (action.type) {
    case "setLocale":
      return setLocale(state, action.locale);
    case "setUser":
      return { ...state, user: action.user };
    case "setLeads":
      return {
        ...state,
        leads: action.leads
      };
    case "setInstitution":
      return {
        ...state,
        institution: action.institution
      };
    case "setInstitutionsList":
      return {
        ...state,
        institutionsList: action.institutionsList
      };

    case "setSelectedInstitution":
      return {
        ...state,
        selectedInstitution: action.selectedInstitution
      };
    case "setNodeAccess":
      return {
        ...state,
        nodeAccess: false
      };
    case "setMembership":
      return {
        ...state,
        membership: action.membership
      };
    case "setComponentLoaded":
      componentLoaded[action.component] = true;
      return { ...state, componentLoaded: componentLoaded };
    case "setPreviewModalStatus":
      componentLoaded.openPreviewModal = false;
      return { ...state, componentLoaded: componentLoaded };
    case "SetSelectedTabView":
      return { ...state, tab: action.tab };
    case "setLightcastPreviewFlag":
      return {
        ...state,
        institution: {
          ...state.institution,
          showLightcastPreview: action.showLightcastPreview
        }
      };
    case "setIntegratedProducts":
      return state.integratedProducts
        ? state
        : { ...state, integratedProducts: action.integratedProducts };
    case "setNodeAccessContributor":
      return {
        ...state,
        nodeAccessContributor: action.nodeAccessContributor
      };
    case "setBreadcrumb":
      return {
        ...state,
        breadcrumb: action.breadcrumb
      };
    case "setShowUnauthorized":
      return {
        ...state,
        showUnauthorized: action.showUnauthorized
      };
    case "setCesUserRole":
      return {
        ...state,
        cesUserRole: action.cesUserRole
      };
    case "setOutcomesTerminology":
      return {
        ...state,
        outcomesTerminology: action.outcomesTerminology
      };
    case "setSuccessMetricMessage":
      return {
        ...state,
        successMetricMessage: action.successMetricMessage
      };
    case "setChatStreaming":
      return {
        ...state,
        isChatStreaming: action.isChatStreaming
      };
    case "setChatQuotaExhausted":
      return {
        ...state,
        isChatQuotaExhausted: action.isChatQuotaExhausted
      };
    case "setAiChatToggleVisible":
      return {
        ...state,
        isAiChatToggleVisible: action.isAiChatToggleVisible
      };
    default:
      return state;
  }
};
const setLocale = (state: State, locale: string) => {
  switch (locale) {
    case "pig": {
      return { ...state, locale: "en-US", messages: pig(ENGLISH) };
    }
    default: {
      return { ...state, locale: "en-US", messages: ENGLISH };
    }
  }
};

const initialState: State = {
  locale: "en-US",
  messages: ENGLISH,
  institution: defaultAction.institution,
  user: defaultInsightsUser,
  leads: [],
  membership: defaultMembership,
  tab: "institution-strategy",
  componentLoaded: {
    selectedTab: "item-1",
    collapseSideNav: false,
    openPreviewModal: false,
    institution: false,
    membership: false
  },
  integratedProducts: null,
  nodeAccess: true,
  nodeAccessContributor: null,
  institutionsList: [],
  selectedInstitution: null,
  breadcrumb: null,
  showUnauthorized: false,
  cesUserRole: null,
  outcomesTerminology: null,
  successMetricMessage: null,
  isChatStreaming: false,
  isChatQuotaExhausted: false,
  isAiChatToggleVisible: false
};

function getUuid(type: "organization" | "institution" | "program"): string | null {
  const { pathname } = window.location;

  const regexMap: { [key in "organization" | "institution" | "program"]: RegExp } = {
    organization: /\/organization\/([a-z0-9\-]{36})/,
    institution: /\/institution\/([a-z0-9\-]{36})/,
    program: /\/program\/([a-z0-9\-]{36})/
  };

  const regex = regexMap[type];
  const match = pathname.match(regex);
  return match ? match[1] : null;
}

function isPlanningRoute(): boolean {
  const { pathname } = window.location;
  return pathname.includes('/planning/');
}

interface SkipMenuVisibilityState {
  showNavigation: boolean;
  showAiAssistant: boolean;
}

const skipMenuVisibilityInitialState: SkipMenuVisibilityState = {
  showNavigation: true,
  showAiAssistant: false
};

function skipMenuVisibilityReducer(
  state: SkipMenuVisibilityState,
  action: SkipMenuVisibilityState
): SkipMenuVisibilityState {
  return action;
}

const SkipMenuManager = () => {
  const location = useLocation();
  const [{ showNavigation, showAiAssistant }, dispatch] = useReducer(
    skipMenuVisibilityReducer,
    skipMenuVisibilityInitialState
  );

  useEffect(() => {
    const updateNavVisibility = () => {
      const navElement = document.getElementById('mainnav');
      dispatch({
        showNavigation: !!navElement && !navElement.classList.contains('hamburger-hide'),
        showAiAssistant: !!document.getElementById('ai-chat-toggle-btn')
      });
    };

    updateNavVisibility();
    window.addEventListener('mainnav-visibility-changed', updateNavVisibility);
    return () => window.removeEventListener('mainnav-visibility-changed', updateNavVisibility);
  }, [location.pathname]);

  return <SkipMenu showNavigation={showNavigation} showAiAssistant={showAiAssistant} />;
};

function App() {
  const { REDIRECT_URI } = runConfig;

  const [state, dispatch] = useReducer(reducer, initialState);

  // Helper function to check if current route needs non-SA integrated products
  const hasSaIntegratedProducts = (pathname?: string) => {
    const path = pathname || window.location.pathname;

    // Check for exact matches and pattern matches
    const patterns = [
      /^\/insights\/home$/, // /insights/home
      /^\/insights\/academic_office$/, // /insights/academic_office
      /^\/insights\/institution\/[^\/]+\/faculty_success\/reporting_year\/[^\/]+\/mapping_id\/[^\/]+\/html$/, // /insights/institution/:institution_uuid/faculty_success/reporting_year/:reporting_year_uuid/mapping_id/:mapping_id/html
      /^\/insights\/planning\/institution\/[^\/]+\/faculty_success\/reporting_year\/[^\/]+\/mapping_id\/[^\/]+\/html$/, // /insights/planning/institution/:institution_uuid/faculty_success/...
      /^\/insights\/institution\/[^\/]+\/sll\/reporting_year\/[^\/]+\/html$/, // /insights/institution/:institution_uuid/sll/reporting_year/:reporting_year_uuid/html
      /^\/insights\/planning\/institution\/[^\/]+\/sll\/reporting_year\/[^\/]+\/html$/, // /insights/planning/institution/:institution_uuid/sll/reporting_year/:reporting_year_uuid/html
      /^\/insights\/institution\/[^\/]+$/,
      /^\/insights\/institution\/[^\/]+\/organization\/[^\/]+$/,
      /^\/insights\/planning\/institution\/[^\/]+$/ // /insights/planning/institution/:institution_uuid
    ];

    return patterns.some(pattern => pattern.test(path));
  };

  const loadCesUserRoleIfNeeded = () => {
    if (state.institution.platformUuid !== "" && !state.cesUserRole) {
      const applyResult = (result: CesUserRole) => {
        dispatch({
          ...defaultAction,
          type: "setCesUserRole",
          cesUserRole: result
        });
      };

      sendRequest(
        { uri: "/ces/ce-user-role", camelize: true },
        applyResult
      );
    }
  };

  // Centralized function to load integrated products if needed
  const loadIntegratedProductsIfNeeded = (currentPath?: string) => {
    const needsIntegratedProducts =
      state.institution.platformUuid !== "" &&
      !state.integratedProducts &&
      hasSaIntegratedProducts(currentPath);

    if (needsIntegratedProducts) {
      const applyResult = (result: Product[]) => {
        setIntegratedProducts(result);
      };

      sendRequest(
        {
          uri: `/home/integrated-products`,
          camelize: true
        },
        applyResult
      );
    }

    return needsIntegratedProducts;
  };

  // Route change detector component (runs inside Router)
  const RouteChangeDetector = () => {
    const location = useLocation();

    useEffect(() => {
      loadIntegratedProductsIfNeeded(location.pathname);
    }, [location.pathname, state.institution.platformUuid, state.integratedProducts]);

    return null; // This component doesn't render anything
  };

  useEffect(() => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const paramlocale = param.get("locale");
    const locale = paramlocale ? paramlocale : "";
    dispatch({ ...defaultAction, type: "setLocale", locale: locale });
  }, []);

  useEffect(() => {
    const applyResult = (result: Institution | null) => {
      if (result) {
        dispatch({
          ...defaultAction,
          type: "setInstitution",
          institution: result
        });
      } else {
        dispatch({
          ...defaultAction,
          type: "setNodeAccess"
        });
      }

      dispatch({
        ...defaultAction,
        type: "setComponentLoaded",
        component: "institution"
      });
    };
    const onError = (error: any) => {
      if (error.status === 403) {
        dispatch({
          ...defaultAction,
          type: "setShowUnauthorized",
          showUnauthorized: true
        });
        dispatch({
          ...defaultAction,
          type: "setComponentLoaded",
          component: "institution"
        });
        dispatch({
          ...defaultAction,
          type: "setComponentLoaded",
          component: "membership"
        })

      } else {
        window.location.href = REDIRECT_URI;
      }
    };

    const organizationUuid = getUuid("organization");
    const institutionUuid = getUuid("institution");
    const programUuid = getUuid("program");

    // Build query parameters more cleanly
    const uuidParams = [
      { key: "organization_uuid", value: organizationUuid },
      { key: "program_uuid", value: programUuid },
      { key: "institution_uuid", value: institutionUuid }
    ];

    const params = uuidParams
      .filter(param => param.value)
      .map(param => `${param.key}=${param.value}`);

    if (isPlanningRoute()) {
      params.push("view=planning");
    }

    const queryString = params.length > 0 ? `?${params.join("&")}` : "";

    sendRequest(
      {
        uri: `/sa/institution${queryString}`,
        camelize: true
      },
      applyResult,
      onError
    );
  }, [REDIRECT_URI]);

  useEffect(() => {
    if (state.selectedInstitution) {
      const applyResult = () => {
        window.location.href = "/insights/home";
      };

      sendRequest(
        {
          uri: `/switch-institution/${state.selectedInstitution}`,
          camelize: true
        },
        applyResult
      );
    }
  }, [state.selectedInstitution]);

  useEffect(() => {
    if (
      state.institution.platformUuid !== ""
    ) {
      const applyResult = (result: UserInstitution[]) => {
        if (result) {
          dispatch({
            ...defaultAction,
            type: "setInstitutionsList",
            institutionsList: result
          });
        }
      };
      const params = [];
      if (isPlanningRoute()) {
        params.push("view=planning");
      }
  

      sendRequest(
        {
          uri: `/sa/user-institutions?${params.join("&")}`,
          camelize: true
        },
        applyResult
      );
    }
  }, [state.institution]);

  useEffect(() => {
    if (state.institution.platformUuid !== "") {
      const applyResult = (result: InsightsUser) => {
        dispatch({ ...defaultAction, type: "setUser", user: result });
        dispatch({
          ...defaultAction,
          type: "setComponentLoaded",
          component: "membership"
        });
      };
      sendRequest({ uri: "/sa/user_info", camelize: true }, applyResult);
    }
  }, [state.institution.platformUuid]);

  useEffect(() => {
    if (state.user && state.user.gainsightData && state.institution.gainsightEnabled) {
      initiateGainsight(state.user.gainsightData);
    }
  }, [state.user, state.institution]);

  useEffect(() => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const paramlocale = param.get("locale");
    const tab = param.get("tab");
    const locale = paramlocale ? paramlocale : "";
    dispatch({ ...defaultAction, type: "setLocale", locale: locale });
    dispatch({
      ...defaultAction,
      type: "SetSelectedTabView",
      tab: tab || ""
    });
  }, []);

  // Initial load of integrated products and CES user role for non-planning routes
  useEffect(() => {
    loadIntegratedProductsIfNeeded();
    loadCesUserRoleIfNeeded();
  }, [state.institution.platformUuid]);

  const onWmTabSelectedEvent = (event: any) => {
    if (
      event.detail.tabId === "labor-market-insights" &&
      state.institution.showLightcastPreview
    ) {
      dispatch({
        ...defaultAction,
        type: "setComponentLoaded",
        component: "openPreviewModal"
      });
      dispatch({
        ...defaultAction,
        type: "setLightcastPreviewFlag",
        showLightcastPreview: false
      });
    }
    dispatch({
      ...defaultAction,
      type: "SetSelectedTabView",
      tab: event.detail.tabId
    });
  };

  const setSelectedTabView = (tabId: string) => {
    dispatch({
      ...defaultAction,
      type: "SetSelectedTabView",
      tab: tabId
    });
  };

  const setIntegratedProducts = (result: Product[]) => {
    dispatch({
      ...defaultAction,
      type: "setIntegratedProducts",
      integratedProducts: result
    });
  };

  const setOutcomesTerminology = (result: OutcomesTerminology) => {
    dispatch({
      ...defaultAction,
      type: "setOutcomesTerminology",
      outcomesTerminology: result
    });
  };

  const initialContext = {
    institution: state.institution,
    selectedTab: state.tab,
    onWmTabSelectedEvent: onWmTabSelectedEvent,
    sysUser: state.user,
    leads: state.leads,
    componentLoaded: state.componentLoaded,
    membership: state.membership,
    integratedProducts: state.integratedProducts,
    institutionsList: state.institutionsList,
    selectedInstitution: state.selectedInstitution,
    breadcrumb: state.breadcrumb,
    cesUserRole: state.cesUserRole,
    outcomesTerminology: state.outcomesTerminology,
    setBreadcrumb: (breadcrumb: string) => {
      dispatch({
        ...defaultAction,
        type: "setBreadcrumb",
        breadcrumb
      });
    },
    setSelectedInstitution: (institution: UserInstitution) => {
      dispatch({
        ...defaultAction,
        type: "setSelectedInstitution",
        selectedInstitution: institution.uuid
      });
    },
    setComponentLoaded: (component: string) => {
      dispatch({
        ...defaultAction,
        type: "setComponentLoaded",
        component: component
      });
    },
    setleads: (result: Lead[]) => {
      dispatch({ ...defaultAction, type: "setLeads", leads: result });
    },
    setMembership: (result: Membership) => {
      dispatch({ ...defaultAction, type: "setMembership", membership: result });
    },
    setOutcomesTerminology: setOutcomesTerminology,
    successMetricMessage: state.successMetricMessage,
    openWithSuccessMetricMessage: (message: string) => {
      dispatch({
        ...defaultAction,
        type: "setSuccessMetricMessage",
        successMetricMessage: message
      });
    },
    clearSuccessMetricMessage: () => {
      dispatch({
        ...defaultAction,
        type: "setSuccessMetricMessage",
        successMetricMessage: null
      });
    },
    isChatStreaming: state.isChatStreaming,
    setChatStreaming: (isChatStreaming: boolean) => {
      dispatch({
        ...defaultAction,
        type: "setChatStreaming",
        isChatStreaming
      });
    },
    isChatQuotaExhausted: state.isChatQuotaExhausted,
    setChatQuotaExhausted: (isChatQuotaExhausted: boolean) => {
      dispatch({
        ...defaultAction,
        type: "setChatQuotaExhausted",
        isChatQuotaExhausted
      });
    },
    isAiChatToggleVisible: state.isAiChatToggleVisible,
    setAiChatToggleVisible: (isAiChatToggleVisible: boolean) => {
      dispatch({
        ...defaultAction,
        type: "setAiChatToggleVisible",
        isAiChatToggleVisible
      });
    },
    isAiAssistantAvailable: state.isAiChatToggleVisible,
    closePreviewModal: () => {
      const applyResult = (result: any) => { };
      sendRequest(
        { uri: "/lightcast/lightcast_preview_shown", camelize: true },
        applyResult
      );

      dispatch({ ...defaultAction, type: "setPreviewModalStatus" });
    }
  };

  const ifAdministrator = state.user.roles.includes("administrator");

  const ifInsightsContributor =
    state.user.roles.includes("contributor") &&
    state.user.roles.length === 1;

  useEffect(() => {
    if (ifInsightsContributor) {
      const applyResult = (result: NodeAccess) => {
        dispatch({
          ...defaultAction,
          type: "setNodeAccessContributor",
          nodeAccessContributor: result
        });
      };
      sendRequest({ uri: "/sa/nodes_access", camelize: true }, applyResult);
    }
  }, [ifInsightsContributor]);

  const isContributorUserWithInstitutionAccess =
    ifInsightsContributor &&
    state.nodeAccessContributor !== null &&
    state.nodeAccessContributor.institution.length > 0;

  const isContributorUserWithOrganizationAccess =
    ifInsightsContributor &&
    state.nodeAccessContributor !== null &&
    state.nodeAccessContributor.organizations.length > 0;

  const authForCESHtml =
    state.institution.showCourseEvaluation &&
    (isContributorUserWithInstitutionAccess ||
      isContributorUserWithOrganizationAccess ||
      !ifInsightsContributor);

  const authForSllHtml =
    state.integratedProducts?.some((product) => product.id === "sll") &&
    (isContributorUserWithInstitutionAccess || !ifInsightsContributor);

  const authForFacultyHtml = state.integratedProducts?.some(
    (product) =>
      product.id === "fs" &&
      (isContributorUserWithInstitutionAccess ||
        isContributorUserWithOrganizationAccess ||
        !ifInsightsContributor)
  );

  const isPSSUserOrLoadedInsightsUser = state.user && state.user.uuid !== "";

  const orgProgramsPageAuth =
    ifAdministrator ||
    (ifInsightsContributor &&
      state.institution?.isSllSysAdminIntegrated &&
      state.nodeAccessContributor !== null &&
      state.nodeAccessContributor.programs.length > 0);

  const institutionPageAuth =
    ifAdministrator || isContributorUserWithInstitutionAccess;

  const organizationPageAuth =
    ifAdministrator || isContributorUserWithOrganizationAccess;

  const isNodeAccessDataRequired =
    ifAdministrator || isPlanningRoute() ||
    (ifInsightsContributor && state.nodeAccessContributor);

  const isPSSHtmlPage = () => {
    const path = window.location.pathname;
    return path.endsWith("html");
  };

  const isMainComponentLoaded = () => {
    return (
      state.componentLoaded.institution &&
      (isPSSHtmlPage() ? true : state.componentLoaded.membership)
    );
  };

  return (
    <IntlProvider locale={state.locale} messages={state.messages}>
      {state.showUnauthorized && <UnauthorizedComponent />}
      <>
        {!isMainComponentLoaded() && <Loader />}
        <AppContext.Provider value={initialContext}>
          {state.nodeAccess ? (
            state.messages &&
            state.institution &&
            state.institution.platformUuid != "" &&
            (state.integratedProducts || !hasSaIntegratedProducts()) &&
            isNodeAccessDataRequired &&
            (isPSSUserOrLoadedInsightsUser ? (
              <>
                <Router>
                  <SkipMenuManager />
                  <RouteChangeDetector />
                  <Routes>
                    <Route
                      path="/insights/institution/:institution_uuid/reporting_year/:reporting_year_uuid/plan/:plan_uuid/html"
                      element={<PlanView />}
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid/reporting_year/:reporting_year_uuid/plan/:plan_uuid/html"
                      element={<PlanView />}
                    />
                    <Route
                      path="/insights/institution/:institution_uuid/labor_market/html"
                      element={<LaborMarketExportView />}
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid/labor_market/html"
                      element={<LaborMarketExportView />}
                    />
                    <Route
                      path="/insights/institution/:institution_uuid/faculty_success/reporting_year/:reporting_year_uuid/mapping_id/:mapping_id/html"
                      element={
                        <FacultySuccessExportView auth={authForFacultyHtml} />
                      }
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid/faculty_success/reporting_year/:reporting_year_uuid/mapping_id/:mapping_id/html"
                      element={
                        <FacultySuccessExportView auth={authForFacultyHtml} />
                      }
                    />
                    <Route
                      path="/insights/institution/:institution_uuid/institution-strategy/html"
                      element={<MissionAndOutcomesExportView />}
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid/institution-strategy/html"
                      element={<MissionAndOutcomesExportView />}
                    />
                    <Route
                      path="/insights/institution/:institution_uuid/sll/reporting_year/:reporting_year_uuid/html"
                      element={<SllExportView auth={authForSllHtml} />}
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid/sll/reporting_year/:reporting_year_uuid/html"
                      element={<SllExportView auth={authForSllHtml} />}
                    />
                    <Route
                      path="/insights/institution/:institution_uuid/ces/survey/:survey_id/project/:project_id/html"
                      element={<CesHtmlView auth={authForCESHtml} />}
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid/ces/survey/:survey_id/project/:project_id/html"
                      element={<CesHtmlView auth={authForCESHtml} />}
                    />
                    <Route
                      path="/insights/academic_office"
                      element={
                        <InsightsHeaderSidebar
                          user={state.user}
                          children={
                            <AcademicsAndOffices auth={ifAdministrator} />
                          }
                        />
                      }
                    />
                    <Route
                      path="/insights/home"
                      element={
                        <InsightsHeaderSidebar
                          user={state.user}
                          children={<HomeComponent />}
                        />
                      }
                    />
                    <Route
                      path="/insights/settings"
                      element={
                        <InsightsHeaderSidebar
                          user={state.user}
                          children={<InsightsSettings auth={ifAdministrator} />}
                        />
                      }
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid/program/:program_uuid"
                      element={
                        <PlanningHeaderSidebar
                          institution={state.institution}
                          componentLoaded={state.componentLoaded}
                          children={
                            <OrgInsightsBody
                              auth={state.institution.isPssIntegrated}
                              productView="planning"
                            />
                          }
                        />
                      }
                    />
                    <Route
                      path="/insights/institution/:institution_uuid/program/:program_uuid"
                      element={
                        <InsightsHeaderSidebar
                          user={state.user}
                          hideNavigatorSideNav={true}
                          children={
                            <OrgInsightsBody auth={orgProgramsPageAuth} productView="navigator" />
                          }
                        />
                      }
                    />
                    <Route
                      path="/insights/institution/:institution_uuid/organization/:organization_uuid"
                      element={
                        <InsightsHeaderSidebar
                          user={state.user}
                          hideNavigatorSideNav={true}
                          children={
                            <OrgDetailComponent
                              setSelectedTabView={setSelectedTabView}
                              auth={organizationPageAuth}
                              productView="navigator"
                            />
                          }
                        />
                      }
                    />
                    <Route
                      path="/insights/institution/:institution_uuid"
                      element={
                        <InsightsHeaderSidebar
                          user={state.user}
                          hideNavigatorSideNav={true}
                          showAiChat={true}
                          children={
                            <OrgProfileComponent
                              setSelectedTabView={setSelectedTabView}
                              auth={institutionPageAuth}
                            />
                          }
                        />
                      }
                    />
                    <Route
                      path="/insights/planning/institution/:institution_uuid"
                      element={
                        <PlanningHeaderSidebar
                          institution={state.institution}
                          componentLoaded={state.componentLoaded}
                          showAiChat={true}
                          children={
                            <Body
                              setSelectedTabView={setSelectedTabView}
                              auth={state.institution.isPssIntegrated}
                              productView="planning"
                            />
                          }
                        />
                      }
                    />
                    {process.env.NODE_ENV === 'development' && process.env.MIRAGE_ENV === 'true' && (
                      <>
                        <Route
                          path="/insights/mirage-state/manage"
                          element={
                            <Suspense fallback={<Loader />}>
                              <MirageStateComponent />
                            </Suspense>
                          }
                        />
                      </>
                    )}
                    <Route
                      path="/insights/sitemap"
                      element={
                        <InsightsHeaderSidebar
                          user={state.user}
                          children={<SiteMap />}
                        />
                      }
                    />
                    <Route
                      path="/insights/unauthorized"
                      element={<UnauthorizedComponent />}
                    />
                    <Route path="/" element={<Navigate
                      to="/insights/home"
                      replace
                    />} />
                    <Route path="*" element={<IncorrectURLComponent />} />
                  </Routes>
                </Router>
              </>
            ) : (
              <Loader />
            ))
          ) : (
            <UnauthorizedComponent />
          )}
        </AppContext.Provider>
      </>
    </IntlProvider>
  );
}

export default App;
